import { BackendCutlistGroup } from '@cutr/constants/cutlist-backend';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';

import { agentApi } from '@/api/backend/agent';
import { useMaterialGroupState } from '@/api/materialsGroup';
import {
  computeCutlistNestingHash,
  useMixedNestingStore,
} from '@/api/mixedNesting';
import { partsSelector, useCutlistState } from '@/api/store';
import { isStarterTier } from '@/theme';

import { cutlistsKeys } from './keys';
import { agentKeys } from './keys';

const startNestingPolling = (
  queryClient: QueryClient,
  cutlistId: string
): void => {
  if (!isStarterTier()) return;

  const { setPolling, setNestingState, setSnapshotHash } =
    useMixedNestingStore.getState();

  setSnapshotHash(
    computeCutlistNestingHash(
      useMaterialGroupState.getState().groups,
      partsSelector(useCutlistState.getState())
    )
  );
  queryClient.removeQueries({ queryKey: agentKeys.nestingJobs(cutlistId) });
  setNestingState('computing');
  setPolling(true);
};

export const useCreateMaterialGroup = (cutlistId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (materialGroup: BackendCutlistGroup) =>
      agentApi.createMaterialGroup(cutlistId, materialGroup),
    mutationKey: agentKeys.createMaterialGroup,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: cutlistsKeys.getOne(cutlistId),
        refetchType: 'all',
      });
      queryClient.removeQueries({
        queryKey: ['agent', 'price', 'override', cutlistId],
        exact: false,
      });
      startNestingPolling(queryClient, cutlistId);
    },
  });
};

export const useUpdateMaterialGroup = (cutlistId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      materialGroupId,
      materialGroup,
    }: {
      materialGroupId: string;
      materialGroup: BackendCutlistGroup;
    }) =>
      agentApi.updateMaterialGroup(cutlistId, materialGroupId, materialGroup),
    mutationKey: agentKeys.updateMaterialGroup,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: cutlistsKeys.getOne(cutlistId),
        refetchType: 'all',
      });
      queryClient.removeQueries({
        queryKey: ['agent', 'price', 'override', cutlistId],
        exact: false,
      });
      startNestingPolling(queryClient, cutlistId);
    },
  });
};

export const useIsMutatingMaterialGroup = () => {
  const isCreating =
    useIsMutating({ mutationKey: agentKeys.createMaterialGroup }) > 0;
  const isUpdating =
    useIsMutating({ mutationKey: agentKeys.updateMaterialGroup }) > 0;
  const isDeleting =
    useIsMutating({ mutationKey: agentKeys.deleteMaterialGroup }) > 0;

  return isCreating || isUpdating || isDeleting;
};

export const useDeleteMaterialGroup = (cutlistId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (materialGroupId: string) =>
      agentApi.deleteMaterialGroup(cutlistId, materialGroupId),
    mutationKey: agentKeys.deleteMaterialGroup,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: cutlistsKeys.getOne(cutlistId),
        refetchType: 'all',
      });
      queryClient.removeQueries({
        queryKey: ['agent', 'price', 'override', cutlistId],
        exact: false,
      });
    },
  });
};
